import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { IDeviceShadowRecord } from 'src/app/shared/services/device-shadow/device-shadow.service';
import { DeviceViewService } from 'src/app/shared/services/device-view/device-view.service';
import { DeviceVersion } from 'src/app/shared/services/versions/versions.service';
import { StatusColor } from 'src/app/shared/status-indicator/status-indicator.component';
import { DeviceEvent, IDevicePhoto } from '../../services/monitoring.service';
import { DeviceEventModalComponent } from '../device-event-modal-section/device-event-modal.component';
import {
  HubAuxModalComponent,
  HubAuxModalResult,
} from '../../hub-aux-modal/hub-aux-modal.component';
import {
  LiveImageService,
  LiveImageResponse,
} from 'src/app/shared/services/live-image/live-image.service';

interface CameraTile {
  name: string;
  label: string;
  url?: string;
}

@Component({
  selector: 'device-metadata-section',
  templateUrl: './device-metadata-section.component.html',
  styleUrls: ['./device-metadata-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DeviceMetadataSectionComponent
  implements OnInit, OnChanges, OnDestroy
{
  private static readonly pendingDissociations = new Set<string>();

  private static readonly STATE_COLOR_MAP: Record<string, string> = {
    Active: '#1B8E6D',
    Awake: '#00C6D2',
    'Awake (Countdown)': '#71B3B4',
    Communicating: '#4C84FF',
    Hibernating: '#7D7FFF',
    'Deep Sleep': '#4A2DA6',
    Offline: '#F94E4E',
  };

  private static readonly BATTERY_HEALTH_LABELS: Record<string, string> = {
    Good: 'Normal',
    Weak: 'Weak',
    Critical: 'Faulty',
    'Not Charging': 'Not Charging',
    Unknown: 'Unknown',
  };

  private static readonly BATTERY_HEALTH_COLORS: Record<string, string> = {
    Good: '#00B884',
    Weak: '#FFD600',
    Critical: '#F94E4E',
    'Not Charging': '#F94E4E',
  };

  @Input() metadata: IDeviceShadowRecord;
  @Input() geotabMetadata: IDeviceShadowRecord;
  @Input() photos: IDevicePhoto[];
  @Input() deviceVersion: DeviceVersion;
  @Input() deviceEvent: DeviceEvent[];
  @Input() deviceEncryptionKey: string;
  @Output() dissociated = new EventEmitter<void>();
  @Output() dissociationConfirmed = new EventEmitter<void>();

  private static readonly NO_SIGNAL_THRESHOLD_HOURS = 24;
  private viewsPollIntervalId: number | null = null;
  private auxViewsPollIntervalId: number | null = null;
  private destroyed = false;

  photoTakenDate: string;
  cameraTiles: CameraTile[] = [];
  settings: Object = {
    selector: '.gallery-item',
  };

  hubDissociationPending = false;
  requestingImages = false;
  liveImageError: string | null = null;

  private static readonly REQUEST_FAILED_MESSAGE =
    'Unable to retrieve image. Please try again.';
  private static readonly MAIN_VIEWS = ['FORWARD', 'REAR'];
  private static readonly OFFLINE_STATES = ['Offline', 'Deep Sleep'];

  constructor(
    private modalService: NgbModal,
    private deviceViewService: DeviceViewService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private liveImageService: LiveImageService,
  ) {}

  ngOnInit() {
    this.buildCameraTiles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metadata']) {
      this.updatePendingState();
      // Reload cached images whenever the bound device changes (the timeline reuses this
      // component across serials), so we don't show the previous device's images.
      const prevSerial = changes['metadata'].previousValue?.serialNumber;
      const currSerial = changes['metadata'].currentValue?.serialNumber;
      if (currSerial && currSerial !== prevSerial) {
        void this.loadLiveImages();
      }
    }
    if (changes['metadata'] || changes['photos']) {
      this.buildCameraTiles();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopViewsPolling();
    this.stopAuxViewsPolling();
  }

  private updatePendingState(): void {
    const serialNumber = this.metadata?.serialNumber;
    if (!serialNumber) return;
    if (!this.metadata?.followerSerial) {
      DeviceMetadataSectionComponent.pendingDissociations.delete(serialNumber);
      this.hubDissociationPending = false;
    } else {
      this.hubDissociationPending =
        DeviceMetadataSectionComponent.pendingDissociations.has(serialNumber);
    }
  }

  private startViewsPolling(serialNumber: string): void {
    this.stopViewsPolling();
    this.viewsPollIntervalId = window.setInterval(async () => {
      try {
        const response = await this.deviceViewService
          .getDeviceViews(serialNumber)
          .toPromise();
        if (!response?.followerSerialNumber) {
          this.stopViewsPolling();
          if (this.destroyed) return;
          DeviceMetadataSectionComponent.pendingDissociations.delete(
            serialNumber,
          );
          this.toastr.success('Hub dissociation successful');
          this.dissociationConfirmed.emit();
          this.cdr.markForCheck();
        }
      } catch (err) {
        console.error('Error polling device views:', err);
      }
    }, 10000);
  }

  private stopViewsPolling(): void {
    if (this.viewsPollIntervalId !== null) {
      window.clearInterval(this.viewsPollIntervalId);
      this.viewsPollIntervalId = null;
    }
  }

  get enableBatteryView(): boolean {
    return new URLSearchParams(window.location.search).get('enableBatteryView') === 'true';
  }

  private startAuxViewsPolling(viewNames: string[]): void {
    const serialNumber = this.metadata?.serialNumber;
    if (!serialNumber || !viewNames.length) return;
    this.stopAuxViewsPolling();
    const pending = new Set(viewNames.map((name) => name.toUpperCase()));
    this.auxViewsPollIntervalId = window.setInterval(async () => {
      try {
        const response = await this.deviceViewService
          .getDeviceViews(serialNumber)
          .toPromise();
        const views = response?.views ?? [];
        const stillAssociated = views.some(
          (view) =>
            pending.has(view.name?.toUpperCase() ?? '') &&
            !view.viewDisassociationAcknowledgedDate,
        );
        if (!stillAssociated) {
          this.stopAuxViewsPolling();
          if (this.destroyed) return;
          this.toastr.success('Aux dissociation successful');
          // Reload the profile so it renders the updated (dissociated) view data.
          this.dissociationConfirmed.emit();
          this.cdr.markForCheck();
        }
      } catch (err) {
        console.error('Error polling aux views:', err);
      }
    }, 10000);
  }

  private stopAuxViewsPolling(): void {
    if (this.auxViewsPollIntervalId !== null) {
      window.clearInterval(this.auxViewsPollIntervalId);
      this.auxViewsPollIntervalId = null;
    }
  }

  onDeviceEventSearchClick() {
    const modal = this.modalService.open(DeviceEventModalComponent, {
      size: 'xl',
      windowClass: 'deviceEventModal',
    });
    modal.componentInstance.serialNumber = this.metadata.serialNumber;
  }

  // The Images section shows only the ER's FORWARD/REAR (aux lives in the Hub & Aux modal).
  // A tile with no url renders a "No Signal" placeholder instead of an image.
  private buildCameraTiles(): void {
    this.cameraTiles = DeviceMetadataSectionComponent.MAIN_VIEWS.map((name) => {
      const photo = (this.photos ?? []).find(
        (p) => p.viewName?.toUpperCase() === name && !!p.url,
      );
      return { name, label: this.getDisplayName(name), url: photo?.url };
    });

    // Capture date reflects the shown ER views; reset it when none have an image so a
    // previous device's date doesn't linger.
    const mainPhoto = this.cameraTiles
      .filter((t) => !!t.url)
      .map((t) =>
        (this.photos ?? []).find(
          (p) => p.viewName?.toUpperCase() === t.name && !!p.url,
        ),
      )
      .find((p) => !!p);
    this.photoTakenDate = mainPhoto?.savedDate
      ? new Date(mainPhoto.savedDate).toLocaleString()
      : '';
    }

  getDisplayName(viewName: string): string {
    const upper = viewName?.toUpperCase() ?? '';
    if (upper === 'FORWARD') return 'Outside';
    if (upper === 'REAR') return 'Inside';
    const aux = upper.match(/ANALOG_AUX_(\d+)/);
    if (aux) return `Aux ${aux[1]}`;
    return viewName;
  }

  get isErOffline(): boolean {
    return DeviceMetadataSectionComponent.OFFLINE_STATES.includes(
      this.metadata?.operationalState ?? '',
    );
  }

  // On load, pull cached FORWARD/REAR images from Redis via the live-image API (no new capture).
  private async loadLiveImages(): Promise<void> {
    const serial = this.metadata?.serialNumber;
    if (!serial) return;
    this.liveImageError = null;
    try {
      const result = await this.liveImageService.getLiveImages(
        serial,
        DeviceMetadataSectionComponent.MAIN_VIEWS,
        false,
        false,
      );
      if (result.status === 'COMPLETE' && result.images.length) {
        this.applyLiveImages(result.images);
      }
    } catch {
      // Leave whatever photos were already provided.
    }
    this.cdr.markForCheck();
  }

  async onRequestNewImage(): Promise<void> {
    const serial = this.metadata?.serialNumber;
    if (!serial || this.requestingImages) return;

    this.requestingImages = true;
    this.liveImageError = null;
    this.cdr.markForCheck();

    try {
      const result = await this.liveImageService.requestNewImages(
        serial,
        DeviceMetadataSectionComponent.MAIN_VIEWS,
        false,
      );
      const retrieved =
        result.status === 'COMPLETE' &&
        result.images.some((i) => !!i.signedUrl);
      if (retrieved) {
        this.applyLiveImages(result.images);
      } else {
        this.liveImageError =
          DeviceMetadataSectionComponent.REQUEST_FAILED_MESSAGE;
      }
    } catch {
      this.liveImageError =
        DeviceMetadataSectionComponent.REQUEST_FAILED_MESSAGE;
    } finally {
      this.requestingImages = false;
      this.cdr.markForCheck();
    }
  }

  // Merge returned FORWARD/REAR images into `photos`; cameraTiles is rebuilt from them.
  private applyLiveImages(images: LiveImageResponse[]): void {
    images.forEach((img) => {
      const name = img.name?.toUpperCase();
      if (!name) return;
      // Drop any prior entry for this view, then re-add only when a fresh image is available,
      // so views that came back without an image stop showing.
      const without = (this.photos ?? []).filter(
        (p) => p.viewName?.toUpperCase() !== name,
      );
      this.photos = img.signedUrl
        ? [
            ...without,
            {
              viewName: img.name,
              url: img.signedUrl,
              savedDate: img.photoSavedDate,
            },
          ]
        : without;
    });
    this.buildCameraTiles();
  }

  getModuleStatusColor(status: string): StatusColor {
    switch (status) {
      case 'Installed':
        return StatusColor.green;
      case 'Pending':
        return StatusColor.yellow;
      default:
        return undefined;
    }
  }

  getIntendedFirmwareVersion(): string {
    return (
      this.deviceVersion?.firmware?.expectedVersion?.version ??
      this.metadata?.intendedFirmwareVersion
    );
  }

  getBatteryHealthLabel(): string {
    const raw = (this.metadata as unknown as Record<string, string>)?.['batteryHealth'];
    if (!raw) return '';
    return DeviceMetadataSectionComponent.BATTERY_HEALTH_LABELS[raw] ?? raw;
  }

  getBatteryHealthColor(): string {
    const raw = (this.metadata as unknown as Record<string, string>)?.['batteryHealth'];
    if (!raw) return '';
    return DeviceMetadataSectionComponent.BATTERY_HEALTH_COLORS[raw] ?? '';
  }

  getBatteryFieldValue(field: string): string | number | null {
    const bag = this.metadata as unknown as Record<string, unknown>;
    const value = bag?.[field];
    if (value == null) return null;
    if (typeof value === 'object' && value !== null && 'value' in value) {
      const inner = (value as { value?: string | number | null }).value;
      return inner ?? null;
    }
    return value as string | number;
  }

  getOperatingStateColor(): string {
    const backendState = this.metadata?.operationalState;
    if (!backendState) return 'black';
    return (
      DeviceMetadataSectionComponent.STATE_COLOR_MAP[backendState] || 'black'
    );
  }

  formatLatLongWithDegree(value: any): string {
    if (typeof value !== 'string') {
      value = value != null ? String(value) : '';
    }
    return value
      .split(',')
      .map((part) => part.trim() + '°')
      .join(', ');
  }

  async onViewHubAuxClick(): Promise<void> {
    const serialNumber = this.metadata?.serialNumber as string;
    // Always fetch fresh views so the modal reflects the latest state — this
    // ensures aux views that were just disassociated are correctly hidden on
    // reopen, without waiting for the parent to refresh the full shadow record.
    let freshMetadata = this.metadata;
    try {
      const response = await this.deviceViewService.getDeviceViews(serialNumber).toPromise();

      if (this.hubDissociationPending && !response?.followerSerialNumber) {
        this.stopViewsPolling();
        DeviceMetadataSectionComponent.pendingDissociations.delete(serialNumber);
        this.hubDissociationPending = false;
        this.toastr.success('Hub dissociation successful');
        this.dissociationConfirmed.emit();
        this.cdr.markForCheck();
        return;
      }

      if (response?.views) {
        freshMetadata = { ...this.metadata, views: response.views };
      }
    } catch (err) {
      console.error('Error fetching device views:', err);
    }

    const modal = this.modalService.open(HubAuxModalComponent, {
      centered: true,
      size: 'lg',
    });
    modal.componentInstance.metadata = freshMetadata;
    modal.componentInstance.photos = this.photos ?? [];
    modal.componentInstance.hubDissociationPending = this.hubDissociationPending;
    modal.result.then(
      (result) => this.handleModalResult(result),
      () => {}
    );
  }

  private handleModalResult(result: HubAuxModalResult): void {
    if (result === 'hub-dissociated') {
      const serialNumber = this.metadata.serialNumber as string;
      DeviceMetadataSectionComponent.pendingDissociations.add(serialNumber);
      this.hubDissociationPending = true;
      this.toastr.info('Hub dissociation is in progress');
      this.startViewsPolling(serialNumber);
      this.cdr.markForCheck();
    } else if (
      result &&
      typeof result === 'object' &&
      result.type === 'aux-dissociated'
    ) {
      // The dissociate request was fired but not awaited (it can exceed the gateway
      // timeout). Confirm completion by polling the views API rather than blocking.
      this.toastr.info('Aux dissociation is in progress');
      this.startAuxViewsPolling(result.views);
      this.cdr.markForCheck();
    }
  }

  getLatestDiagnostic(diagnostics: any[]): any {
    if (!diagnostics || diagnostics.length === 0) {
      return null;
    }
    const dtc = diagnostics.reduce((latest, current) =>
      new Date(current.timestamp).getTime() >
      new Date(latest.timestamp).getTime()
        ? current
        : latest,
    );
    return dtc;
  }

  getTelematicsConnectionState(): string {
    const thresholdMs =
      DeviceMetadataSectionComponent.NO_SIGNAL_THRESHOLD_HOURS * 60 * 60 * 1000;
    const withinThreshold =
      !this.geotabMetadata.lastCommunicationDate ||
      new Date().getTime() -
        new Date(this.geotabMetadata.lastCommunicationDate).getTime() <=
        thresholdMs;
    return this.geotabMetadata.connectionState && withinThreshold
      ? 'Connected'
      : 'Disconnected';
  }

  getAuxViews(): any[] {
    if (!this.metadata?.views) {
      return [];
    }

    return this.metadata.views.filter(
      (view: any) =>
        // Aux views only, hiding any already dissociated (acknowledged).
        view.name?.toUpperCase().includes('AUX') && !this.isAuxDissociated(view),
    );
  }

  // Boolean marker check: an aux view is considered dissociated once it carries an acknowledgement date.
  isAuxDissociated(view: { viewDisassociationAcknowledgedDate?: string }): boolean {
    return !!view?.viewDisassociationAcknowledgedDate;
  }

  hasSignal(view: any): boolean {
    if (!view.lastRecordedDate) {
      return false;
    }
    const lastRecorded = new Date(view.lastRecordedDate);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastRecorded.getTime()) / (1000 * 60 * 60);
    return hoursDiff <= DeviceMetadataSectionComponent.NO_SIGNAL_THRESHOLD_HOURS;
  }

  getConnectionStatus(view: any): string {
    return this.hasSignal(view) ? 'Connected' : 'No Signal';
  }

  getConnectionStatusColor(view: any): string {
    return this.hasSignal(view) ? '#1B2431' : '#F94E4E';
  }

  isDissociableState(): boolean {
    return ['Active', 'Awake', 'Awake (Countdown)'].includes(this.metadata?.operationalState);
  }

  isHubConnected(): boolean {
    if (!this.metadata?.hubLastCommunicationDate) return false;
    const hoursDiff = (new Date().getTime() - new Date(this.metadata.hubLastCommunicationDate).getTime()) / (1000 * 60 * 60);
    return hoursDiff <= DeviceMetadataSectionComponent.NO_SIGNAL_THRESHOLD_HOURS;
  }
}
